import { Icon } from '@amzn/awsui-components-core';
import { findIconDefinition, IconLookup, library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LogoConfig } from 'src/components/context/AppContextModels';
import { AppLogoService } from 'src/services/AppLogoService';

library.add(fas);

export type AppLogoSize = 'small' | 'medium';

const SIZE_CLASS_MAP: Record<AppLogoSize, string> = {
  small: 'app-logo--small',
  medium: 'app-logo--medium'
};

/**
 * Renders an app logo from a LogoConfig. Supports three sources:
 * - Cloudscape icon (prefix `cloudscape:`)
 * - FontAwesome solid icon (prefix `fa:`)
 * - S3-hosted image (fetched via presigned URL)
 *
 * @param size - `'small'` (16px, TopNav default) or `'medium'` (larger, e.g. featured cards)
 */
export const AppLogo: React.FC<{ logoConfig: LogoConfig; size?: AppLogoSize }> = ({ logoConfig, size = 'small' }) => {
  const [s3LogoUrl, setS3LogoUrl] = useState<string | null>(null);
  const s3LogoUrlRef = useRef<string | null>(null);
  const { selectedLogoName } = logoConfig;
  const sizeClass = SIZE_CLASS_MAP[size];

  // Derive s3Location for shared logos when not explicitly set
  const s3Location = useMemo(() => {
    if (logoConfig.s3Location) return logoConfig.s3Location;
    if (selectedLogoName?.startsWith('shared:')) {
      return AppLogoService.buildSharedLogoS3Location(selectedLogoName.replace('shared:', ''));
    }
    return null;
  }, [logoConfig.s3Location, selectedLogoName]);

  useEffect(() => {
    if (!s3Location) return;
    let cancelled = false;
    AppLogoService.getLogoPreviewUrl(s3Location).then((url) => {
      if (!cancelled && url) {
        if (s3LogoUrlRef.current) URL.revokeObjectURL(s3LogoUrlRef.current);
        s3LogoUrlRef.current = url;
        setS3LogoUrl(url);
      } else if (url) {
        URL.revokeObjectURL(url);
      }
    });
    return () => {
      cancelled = true;
      if (s3LogoUrlRef.current) {
        URL.revokeObjectURL(s3LogoUrlRef.current);
        s3LogoUrlRef.current = null;
      }
    };
  }, [s3Location?.key]);

  if (selectedLogoName?.startsWith('cloudscape:')) {
    return (
      <Icon
        name={selectedLogoName.replace('cloudscape:', '') as import('@amzn/awsui-components-core/icon').IconProps.Name}
        size={size === 'medium' ? 'big' : 'medium'}
        variant="inverted"
      />
    );
  }

  if (selectedLogoName?.startsWith('fa:')) {
    const lookup: IconLookup = { prefix: 'fas', iconName: selectedLogoName.replace('fa:', '') as any };
    const def = findIconDefinition(lookup);
    return def ? <FontAwesomeIcon icon={def} className={`app-logo-fa ${sizeClass}`} /> : null;
  }

  if (s3LogoUrl) {
    return <img src={s3LogoUrl} alt="App logo" className={`app-logo-img ${sizeClass}`} />;
  }

  return null;
};

/**
 * Renders a static app logo from explicit values (no S3 fetch).
 * Used in preview mode where the presigned URL is already resolved.
 * For shared logos, fetches from S3 if no previewUrl is provided.
 */
export const AppLogoStatic: React.FC<{ selectedLogoName: string | null; previewUrl: string | null; size?: AppLogoSize }> = ({
  selectedLogoName,
  previewUrl,
  size = 'small'
}) => {
  const sizeClass = SIZE_CLASS_MAP[size];
  const [sharedUrl, setSharedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedLogoName?.startsWith('shared:') || previewUrl) return;
    let cancelled = false;
    const s3Location = AppLogoService.buildSharedLogoS3Location(selectedLogoName.replace('shared:', ''));
    AppLogoService.getLogoPreviewUrl(s3Location).then((url) => {
      if (!cancelled && url) setSharedUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedLogoName, previewUrl]);

  if (selectedLogoName?.startsWith('cloudscape:')) {
    return (
      <Icon
        name={selectedLogoName.replace('cloudscape:', '') as import('@amzn/awsui-components-core/icon').IconProps.Name}
        size={size === 'medium' ? 'big' : 'medium'}
        variant="inverted"
      />
    );
  }

  if (selectedLogoName?.startsWith('fa:')) {
    const lookup: IconLookup = { prefix: 'fas', iconName: selectedLogoName.replace('fa:', '') as any };
    const def = findIconDefinition(lookup);
    return def ? <FontAwesomeIcon icon={def} className={`app-logo-fa ${sizeClass}`} /> : null;
  }

  const resolvedUrl = previewUrl || sharedUrl;
  if (resolvedUrl) {
    return <img src={resolvedUrl} alt="App logo" className={`app-logo-img ${sizeClass}`} />;
  }

  return null;
};
